import React, {Component} from "react";
import PropTypes from 'prop-types';
import classNames from "classnames";
import {FieldWrapper} from '../form-validator';
import {round} from '../payment-utils';

/**
 * Amount field for Bootstrap Material design.
 * value is expected to be a number, not a string.
 *
 * This component has a local state to store a value and a displayValue.
 * value is the parsed number of displayValue gets published to the global state of the page.
 * displayValue is what gets displayed and modified by the user.
 */
class AmountField extends Component {
    constructor(props) {
        super(props);
        this.onChange = this.onChange.bind(this);
        this.onBlur = this.onBlur.bind(this);
        // Use local state, because the numeric value does not always matches
        // the entered value (like you enter 2., the numeric value is 2)
        this.state = AmountField.getStateFromValue(this.props.value);
    }

    static getStateFromValue(v) {
        const value = round(typeof v === 'number' ? v : 0);
        const formattedValue = value.toFixed(2);
        // for display use 2 digit precision unless the amount is 0.00
        // that we consider the same as empty
        const displayValue = (formattedValue === '0.00' ? '' : formattedValue);
        // make sure value is rounded to 2 digits
        // console.log('getStateFromValue: AmountField: v=' + v + ', displayValue=' + displayValue);
        return {
            value,
            displayValue
        };
    }

    // prevent user from entering invalid characters for an amount
    // 0-9 and a single dot
    static onKeyPress(e) {
        const key = e.key;
        const strCheck = "1234567890.";
        // 110 is decimal key on the numpad and IE treat that
        // differently than the period inside the strCheck
        if (strCheck.indexOf(key) === -1 && e.keyCode !== 110 && e.charCode !== 46) {
            e.preventDefault();
        }
    }

    onChange(e) {
        const {name, value} = e.target,
            {setValue, postProcess} = this.props;

        /*  returning value as it is in case value === '.' because need to bypass for '.' for use case when user is about to enter suppose only .25 In this case, when just '.' is entered then it should be set in state.value
            Because user is about to enter further but this code breaks in that case as parseFloat returns NaN for '.' and round method just return it as it is and it breaks UI.
            We need to treat this special scenario as a valid, as if we manipulate '.' as invalid and return 0 or something, then user could never enter .25 (which is technically valid)
            we cant even append 0 with . because parseFloat then return 0 for 0. and that would again be problem for user.

            If user just enters . then user will get error on screen to enter valid value.
        */
        let v = value === '.' ? value : round(value ? parseFloat(value.replace(/[^\d.-]/g, '')) : 0);
        // set displayValue first, and if amount changed, publish it also
        if (v !== this.state.value) {
            setValue(name, v, postProcess);
            this.setState({
                value: v,
                displayValue: value
            });
        } else {
            // amount is not changed, update display value only
            this.setState({
                displayValue: value
            });
        }
    }

    // onChange should have already updated the states with the value
    // we just need to update displayValue to make sure it's in currency format xx.xx
    onBlur(e) {
        this.setState(AmountField.getStateFromValue(this.state.value));
    }

    static getDerivedStateFromProps(props, state) {
        // console.log('AmountField.getDerivedStateFromProps: pValue=' + props.value + ', sValue=' + state.value);
        if (props.value !== state.value) {
            return AmountField.getStateFromValue(props.value);
        }
        return null;
    }

    render() {
        // Note that validations is not used here directly,
        // But we have to reference it, so rest does not have.
        // We could have removed it with delete statements also.
        const {
                name, description,
                // extract validations so it does not go into rest
                validations,
                // these fields not use, but we want to remove them from restprops
                // so we don't send them to the field
                value, setValue, postProcess, validationState,
                ...rest
            } = this.props,
            {status, messages} = validationState || {},
            hasError = status === 'error' && Array.isArray(messages),
            readOnly = (typeof this.props.readOnly === 'boolean' ? this.props.readOnly : false);

        return (
            <div className="input-group input-group-minimal md-form form-row form-sm form-3 pl-0 m-sm-0">
                <div className="input-group-prepend">
                    <span className="input-group-text black-text">$</span>
                </div>
                <input type='text'
                       name={name}
                       className={classNames("form-control mt-0 text-right", {'is-invalid': hasError})}
                       id={name}
                       value={this.state.displayValue}
                       placeholder="0.00"
                       onChange={this.onChange}
                       onBlur={this.onBlur}
                       onKeyPress={AmountField.onKeyPress}
                       maxLength="20"
                       autoComplete="off"
                       readOnly={readOnly}
                       {...rest}
                />
                <label htmlFor={name} className={"active"}>
                    {description}
                </label>
                {hasError && messages.map(
                    item => (
                        <div key={item} className="invalid-feedback">
                            {item}
                        </div>
                    ))}
            </div>
        );

    }
}

AmountField.propTypes = {
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    value: PropTypes.number,
    setValue: PropTypes.func.isRequired,
    postProcess: PropTypes.func
};

export default FieldWrapper(AmountField);